import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { StoryService } from '../../../service/story.service';
import { Subscription } from 'rxjs';
import { IStoryDTO } from '../../../dto/story/story.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { DownloadService } from '../../../service/download.service';
import { MediaType } from '../../../dto/enum/media-type.enum';
import { IContentDTO } from '../../../dto/content/content.dto';
import { IPictureStoryDTO } from '../../../dto/story/picture-story.dto';
import { LightGallery } from 'lightgallery/lightgallery';
import lightGallery from 'lightgallery';
import { defaultLightGallerySettings } from '../../../config/light-gallery';
import { GalleryItem } from 'lightgallery/lg-utils';
import { IVideoStoryDTO } from '../../../dto/story/video-story.dto';
import { ILightUserDTO } from '../../../dto/user/light-user.dto';
import { MENTION_SYMBOL } from '../../../util/const';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { NzTypographyComponent } from 'ng-zorro-antd/typography';
import { NzListComponent } from 'ng-zorro-antd/list';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { IUserDTO } from '../../../dto/user/user.dto';
import { AnalyticsService } from '../../../service/analytics.service';
import { SentryService } from '../../../service/sentry.service';
import { unsubscribe } from '../../../util/rx.util';
import { ComponentState } from '../../../dto/enum/component-state.enum';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-stories',
  imports: [
    NzRowDirective,
    NzColDirective,
    NzSpinComponent,
    NzResultComponent,
    NzTypographyComponent,
    NzListComponent,
    NzImageModule,
    NzTooltipDirective,
    NzIconDirective,
  ],
  templateUrl: './stories.component.html',
  styleUrl: './stories.component.less',
})
export class StoriesComponent implements OnInit, OnDestroy {
  public readonly userDTO: InputSignal<IUserDTO> = input.required<IUserDTO>();
  public readonly ComponentState: typeof ComponentState = ComponentState;
  public readonly state: WritableSignal<ComponentState> =
    signal<ComponentState>(ComponentState.LOADING);
  public readonly storyDTOs: WritableSignal<IStoryDTO[]> = signal<IStoryDTO[]>(
    []
  );
  private readonly router: Router = inject(Router);
  private readonly storyService: StoryService = inject(StoryService);
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly downloadService: DownloadService = inject(DownloadService);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private storiesSub$?: Subscription = undefined;
  private lightGallery?: LightGallery = undefined;

  public ngOnInit(): void {
    this.storiesSub$ = this.storyService.fetchStories().subscribe({
      next: (storyDTOs: IStoryDTO[]): void => {
        this.storyDTOs.update((value: IStoryDTO[]): IStoryDTO[] => {
          value.push(
            ...storyDTOs.filter(
              (storyDTO: IStoryDTO): boolean =>
                MediaType.PICTURE === storyDTO.mediaType ||
                MediaType.VIDEO === storyDTO.mediaType
            )
          );
          return value;
        });
        this.state.set(ComponentState.OK);
      },
      error: (error: HttpErrorResponse): void => {
        this.state.set(
          401 === error.status
            ? ComponentState.UNAUTHORIZED_ERROR
            : ComponentState.UNEXPECTED_ERROR
        );
        console.error('An error occurred during fetching stories', error);
        if (401 !== error.status) {
          const userDTO: IUserDTO = this.userDTO();
          this.sentryService.captureException({
            error,
            severityLevel: 'error',
            transactionName: 'FETCH_STORIES_ERROR',
            user: {
              id: userDTO.primaryKey,
              username: userDTO.username,
            },
          });
        }
      },
    });
  }

  public ngOnDestroy(): void {
    unsubscribe(this.storiesSub$);
    this.closeLightGallery();
  }

  public getThumbnailUrl(storyDTO: IStoryDTO): string {
    const contentDTO: IContentDTO =
      MediaType.PICTURE === storyDTO.mediaType
        ? this.getThumbnailOfPictureStoryDTO(storyDTO)
        : this.getThumbnailOfVideoStoryDTO(storyDTO);
    return this.downloadService.getDownloadUrl(contentDTO.url);
  }

  public onImageClick(storyDTOs: IStoryDTO[], index: number): void {
    const galleryItems: GalleryItem[] = storyDTOs.map(
      (storyDTO: IStoryDTO): GalleryItem =>
        MediaType.PICTURE === storyDTO.mediaType
          ? this.getGalleryItemOfPictureStoryDTO(storyDTO)
          : this.getGalleryItemOfVideoStoryDTO(storyDTO)
    );
    const videoGalleryItems: GalleryItem[] = galleryItems
      .filter(
        (galleryItem: GalleryItem) =>
          galleryItem['videoWidth'] && galleryItem['videoHeight']
      )
      .sort(
        (galleryItem0: GalleryItem, galleryItem1: GalleryItem) =>
          galleryItem0['videoWidth'] * galleryItem0['videoHeight'] -
          galleryItem1['videoWidth'] * galleryItem1['videoHeight']
      );
    let videoMaxSize: string | undefined = undefined;
    if (videoGalleryItems.length) {
      const { videoWidth, videoHeight } = videoGalleryItems[0];
      videoMaxSize = `${videoWidth}-${videoHeight}`;
    }
    this.lightGallery = lightGallery(this.document.body, {
      ...defaultLightGallerySettings,
      dynamicEl: galleryItems,
      videoMaxSize,
    });

    const username: string = this.userDTO().username;
    this.document.body.addEventListener(
      'lgAfterClose',
      (): void => {
        this.lightGallery!.destroy();
        this.lightGallery = undefined;
        this.analyticsService.event('gallery_exec', {
          action: 'close',
          content: 'stories',
          user: username,
          path: this.router.url,
        });
      },
      { once: true }
    );

    this.document.body.addEventListener(
      'lgAfterOpen',
      (): void => {
        this.analyticsService.event('gallery_exec', {
          action: 'open',
          content: 'stories',
          user: username,
          path: this.router.url,
        });
      },
      { once: true }
    );

    this.document.body.addEventListener(
      'lgUserClick',
      (event: Event): void => {
        this.closeLightGallery();
        // @ts-ignore
        const username: string = event.detail.username;
        this.router.navigate(['/profiles', username]).then((): void => {
          this.analyticsService.event('user_exec', {
            origin: 'stories',
            username: username,
            path: this.router.url,
          });
        });
      },
      { once: true }
    );

    if (this.document.defaultView) {
      this.document.defaultView.addEventListener(
        'popstate',
        (): void => this.closeLightGallery(),
        { once: true }
      );
    }

    this.lightGallery.openGallery(index);
  }

  public getOtherUsers(storyDTO: IStoryDTO): ILightUserDTO[] {
    const lightUserDTOs: ILightUserDTO[] = [...storyDTO.mentions];
    if (storyDTO.author) {
      if (
        !lightUserDTOs.find(
          (lightUserDTO: ILightUserDTO): boolean =>
            lightUserDTO.primaryKey === storyDTO.author!.primaryKey
        )
      ) {
        lightUserDTOs.push(storyDTO.author);
      }
    }
    return lightUserDTOs;
  }

  public getExpiresAt(storyDTO: IStoryDTO): string {
    return new Date(storyDTO.expiresAt).toLocaleString();
  }

  private getThumbnailOfPictureStoryDTO(storyDTO: IStoryDTO): IContentDTO {
    const pictureStoryDTO: IPictureStoryDTO = storyDTO as IPictureStoryDTO;
    return pictureStoryDTO.images[pictureStoryDTO.images.length - 1];
  }

  private getThumbnailOfVideoStoryDTO(storyDTO: IStoryDTO): IContentDTO {
    const videoStoryDTO: IVideoStoryDTO = storyDTO as IVideoStoryDTO;
    return videoStoryDTO.images[videoStoryDTO.images.length - 1];
  }

  private getGalleryItemOfPictureStoryDTO(storyDTO: IStoryDTO): GalleryItem {
    const pictureStoryDTO: IPictureStoryDTO = storyDTO as IPictureStoryDTO;
    const imageContentDTO: IContentDTO = pictureStoryDTO.images[0];
    const thumbnailContentDTO: IContentDTO =
      this.getThumbnailOfPictureStoryDTO(storyDTO);
    const imageUrl: string = this.downloadService.getDownloadUrl(
      imageContentDTO.url
    );
    const thumbnailUrl: string = this.downloadService.getDownloadUrl(
      thumbnailContentDTO.url
    );
    return {
      id: pictureStoryDTO.primaryKey,
      mediaType: pictureStoryDTO.mediaType,
      src: imageUrl,
      srcWidth: imageContentDTO.width,
      srcHeight: imageContentDTO.height,
      downloadUrl: imageUrl,
      thumb: thumbnailUrl,
      thumbWidth: thumbnailContentDTO.width,
      thumbHeight: thumbnailContentDTO.height,
      width: imageContentDTO.width.toString(),
      alt: pictureStoryDTO.primaryKey,
      subHtml: this.getSubHtml(pictureStoryDTO),
    };
  }

  private getGalleryItemOfVideoStoryDTO(storyDTO: IStoryDTO): GalleryItem {
    const videoStoryDTO: IVideoStoryDTO = storyDTO as IVideoStoryDTO;
    const videoContentDTO: IContentDTO = videoStoryDTO.videos[0];
    const posterContentDTO: IContentDTO = videoStoryDTO.images[0];
    const thumbnailContentDTO: IContentDTO =
      this.getThumbnailOfVideoStoryDTO(storyDTO);
    const videoUrl: string = this.downloadService.getDownloadUrl(
      videoContentDTO.url
    );
    const posterUrl: string = this.downloadService.getDownloadUrl(
      posterContentDTO.url
    );
    const thumbnailUrl: string = this.downloadService.getDownloadUrl(
      thumbnailContentDTO.url
    );
    return {
      id: videoStoryDTO.primaryKey,
      mediaType: videoStoryDTO.mediaType,
      downloadUrl: videoUrl,
      thumb: thumbnailUrl,
      thumbWidth: thumbnailContentDTO.width,
      thumbHeight: thumbnailContentDTO.height,
      poster: posterUrl,
      posterWidth: posterContentDTO.width,
      posterHeight: posterContentDTO.height,
      video: {
        source: [{ src: videoUrl, type: 'video/mp4' }],
        tracks: [],
        // @ts-ignore
        attributes: {
          preload: 'metadata',
          playsInline: true,
          controls: true,
        },
      },
      videoWidth: videoContentDTO.width,
      videoHeight: videoContentDTO.height,
      subHtml: this.getSubHtml(videoStoryDTO),
    };
  }

  private getSubHtml(storyDTO: IStoryDTO): string | undefined {
    const lightUserDTOs: ILightUserDTO[] = this.getOtherUsers(storyDTO);
    let subHtml: string | undefined = undefined;
    if (lightUserDTOs.length) {
      subHtml = lightUserDTOs
        .map(
          (lightUserDTO: ILightUserDTO): string =>
            `<a onclick="document.body.dispatchEvent(new CustomEvent('lgUserClick', {detail: {username: '${lightUserDTO.username}'}}))" class="user">${MENTION_SYMBOL + lightUserDTO.username}</a>`
        )
        .join('');
    }
    return subHtml;
  }

  private closeLightGallery(): void {
    if (this.lightGallery && this.lightGallery.lgOpened) {
      this.lightGallery.closeGallery();
    }
  }
}
